import React, { useEffect, useState } from 'react'

import { HandlerOf } from '../../helpers/typeHelper'
import { useNotes } from '../../hooks/query/useNotes'
import { ApiVersion } from '../../models'
import { Activity, Note, NoteRecordType } from '../../models/note'
import { useUserProvider } from '../Context/UserContext'
import { NotesCard } from './NotesCard'
import { NotesList } from './NotesList'
import { NotesSidebar } from './NotesSidebar'

export interface NotesProps {
  recordId: string
  recordType: NoteRecordType
  recordName?: string
  isTimeline?: boolean
  expandByDefault?: boolean
  isStatic?: boolean
  apiVersion?: ApiVersion
  resetExpandedStateTrigger?: boolean | string | number
  renderHeaderButton?: () => React.ReactNode
  onNotesChange?: HandlerOf<Note[] | Activity[] | undefined>
  children?: (renderOpenNotesCard: () => React.ReactNode) => React.ReactNode
  isInsideSidebar?: boolean
}

export function Notes({
  recordType,
  recordId,
  recordName,
  children,
  isTimeline,
  apiVersion,
  expandByDefault = false,
  resetExpandedStateTrigger,
  renderHeaderButton,
  onNotesChange,
  isInsideSidebar,
  isStatic,
}: NotesProps) {
  const [showOnlyActivity, setShowOnlyActivity] = useState(false)
  const [filterTags, setFilterTags] = useState<string[] | null>(null)
  const {
    data: notes,
    refetch,
    isLoading,
  } = useNotes({ recordType, recordId, isTimeline, tags: filterTags, apiVersion }) // Initial loading of notes
  const expandNotesByDefault = expandByDefault && !isInsideSidebar
  const [isExpanded, setIsExpanded] = useState(expandNotesByDefault)

  const { setIsSideBarCollapsed, loggedInUser } = useUserProvider()
  // Card isn't hidden:
  // if this isn't console, ie. Notes in the app profile page
  // or if it's the notes opener in the sidebar (ie. the slim opener)
  const isNotesCardHidden = Boolean(!loggedInUser?.isTeamAdmin)

  useEffect(() => {
    onNotesChange?.(notes)
    // Only need to check if notes list has changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes?.length, onNotesChange])

  useEffect(() => {
    refetch()
  }, [filterTags, refetch])

  useEffect(() => {
    if (isExpanded) {
      setIsSideBarCollapsed(true)
    }
  }, [isExpanded, setIsSideBarCollapsed])

  useEffect(() => {
    setIsExpanded(expandNotesByDefault)
  }, [expandNotesByDefault, resetExpandedStateTrigger])

  function renderOpenNotesCard() {
    if (isNotesCardHidden) return null

    return (
      <NotesCard
        recordType={recordType}
        recordId={recordId}
        recordName={recordName}
        isTimeline={isTimeline}
        onOpen={() => setIsExpanded(true)}
        apiVersion={apiVersion}
        isSlim={isInsideSidebar}
      />
    )
  }

  function renderNotesList() {
    if (!loggedInUser?.isTeamAdmin) return null

    return (
      <NotesList
        recordType={recordType}
        recordId={recordId}
        recordName={recordName}
        notes={notes}
        refetchNotes={refetch}
        isNotesLoading={isLoading}
        isTimeline={isTimeline}
        showOnlyActivity={showOnlyActivity}
        setShowOnlyActivity={setShowOnlyActivity}
        filterTags={filterTags}
        setFilterTags={setFilterTags}
        isStatic={Boolean(isStatic)}
        apiVersion={apiVersion}
        renderHeaderButton={renderHeaderButton}
      />
    )
  }

  if (!children) {
    return <>{renderNotesList()}</>
  }

  return (
    <NotesSidebar
      isExpanded={isExpanded}
      handleClose={() => setIsExpanded(false)}
      children={() => children(renderOpenNotesCard)}
      renderNotesList={renderNotesList}
    />
  )
}
